/**
 * MODULE: domain/pages/frontend/utils/databaseTableModel.ts
 * PURPOSE: Map database schema + rows to RecordTable shape
 */

import type { DatabaseRow, FieldDefinition } from '../../contracts/pages.types';
import {
  compareFieldValues,
  fieldMatchesSearch,
  isFieldSortable,
  normalizePropertyValue,
} from './databaseFieldUtils';

export function rowsToTableData(
  rows: DatabaseRow[],
  fields: FieldDefinition[]
): Record<string, unknown>[] {
  return rows.map((row) => {
    const record: Record<string, unknown> = { id: row.id };
    for (const field of fields) {
      if (field.field_type === 'created_at') {
        record[field.id] = row.created_at;
      } else if (field.field_type === 'created_by') {
        record[field.id] = '';
      } else {
        record[field.id] = normalizePropertyValue(field, row.properties?.[field.id]);
      }
    }
    return record;
  });
}

export function filterRowsBySearch(
  rows: Record<string, unknown>[],
  fields: FieldDefinition[],
  query: string
): Record<string, unknown>[] {
  const q = query.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((row) =>
    fields.some((field) => fieldMatchesSearch(field, row[field.id], q))
  );
}

export function schemaToColumns(fields: FieldDefinition[]) {
  return fields.map((field) => ({
    key: field.id,
    label: field.name,
    sortable: isFieldSortable(field),
    numeric: field.field_type === 'number',
  }));
}

export function sortTableData(
  rows: Record<string, unknown>[],
  fields: FieldDefinition[],
  sortKey: string | undefined,
  sortDir: 'asc' | 'desc'
): Record<string, unknown>[] {
  if (!sortKey) return rows;
  const field = fields.find((f) => f.id === sortKey);
  if (!field) return rows;

  return [...rows].sort((a, b) => {
    const cmp = compareFieldValues(a[sortKey], b[sortKey], field);
    return sortDir === 'asc' ? cmp : -cmp;
  });
}
